import { artworkUrl } from '@shared/assets/artwork';
import { PUBLIC_SITE_ORIGIN } from '@shared/chrome/copy';
import { ExternalLinkIcon } from '@shared/ui/icons';

import { distinctRoles, PersonFace, RoleBadges } from '../_shared/components';
import type { CreditedItemRow, CreditProfileRecord } from '../_shared/types';

import { copy } from './copy';

/**
 * The person's identity block.
 *
 * **No page head above it, because the `<h1>` is the person's name.** A text
 * head over this would put two first-level headings in one document.
 *
 * **A panel, not a bare row.** The sibling public page opens on the same row and
 * it works there, because a grid of posters follows and gives the page its
 * weight; here a section heading follows immediately, and with nothing between
 * them the identity block reads as a caption for the form below it rather than
 * as the subject of the page.
 *
 * **Only the identity row is side by side.** The face sits beside the name and
 * the slug; the badges and the action take the panel's full width beneath. On a
 * 375 px phone the column left after the face and the panel padding is about
 * 215 px, which breaks the action's label across two lines and drops it under
 * the 44 px target. The face still never sits *above* the name — that is the
 * constraint the shared shape records.
 *
 * The role badges are derived from the credits, not read off the record: a role
 * is a property of the relation.
 */
export function PersonDetailHead({
  person,
  credits,
  creditsReady,
  emptyRoles,
}: {
  person: CreditProfileRecord;
  credits: readonly CreditedItemRow[];
  creditsReady: boolean;
  emptyRoles: string;
}) {
  return (
    /*
      One column on a phone — identity, roles, action — and from `--bp-md` a
      two-column grid where the action sits beside the identity and the badges
      span the row beneath. The document order is the phone reading order and
      the wide layout is placed explicitly, so what a sighted reader sees and
      what a screen reader hears never diverge.
    */
    <header className="mb-7 mt-5 grid gap-4 rounded-md border border-border bg-surface p-5 shadow-1 md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:gap-5 md:p-6">
      <div className="flex items-center gap-4 md:col-start-1 md:row-start-1">
        {/*
          The head reads the SAME column the portrait control writes, so replacing
          the image updates both halves of this screen at once. Resolved through
          `artworkUrl` because the column holds a storage reference rather than a
          browsable URL.
        */}
        <PersonFace
          className="size-[4.5rem] md:size-[5.5rem] lg:size-[7rem]"
          portraitUrl={artworkUrl(person.portrait_url)}
        />
        <div className="grid min-w-0">
          <h1 className="font-display text-h1 font-bold">{person.display_name}</h1>
          {/*
            The slug is this record's only identifier. There is no handle here
            and none anywhere in this feature: a handle is an account
            identifier, and this record has no account.
          */}
          <p className="mono mt-px mb-0 text-xs text-ink-muted">
            <bdi>{person.slug}</bdi>
          </p>
        </div>
      </div>

      {creditsReady ? (
        <p className="mb-0 flex flex-wrap items-center gap-2 md:col-span-2 md:row-start-2">
          <RoleBadges roles={distinctRoles(credits)} empty={emptyRoles} />
        </p>
      ) : null}

      {/*
        Full width on a phone so the target never drops under 44 px, and its
        label's own width from `--bp-sm` — the rule every action row in this
        area follows.
      */}
      <p className="mb-0 md:col-start-2 md:row-start-1 md:justify-self-end">
        <a
          href={`${PUBLIC_SITE_ORIGIN}/people/${encodeURIComponent(person.slug)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="hit-target inline-flex w-full items-center justify-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-5 text-sm font-semibold text-ink no-underline hover:bg-surface-overlay sm:w-auto"
        >
          <ExternalLinkIcon />
          {copy.head.openPublicPage}
        </a>
      </p>
    </header>
  );
}
